import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ListTransactionStatus, ListTypeTransaction, uuidNull } from "@/constants/default";
import { FormProvider, useForm } from "react-hook-form";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useEditTransactionMutation, useGetTransactionQuery } from "@/services/transaction.service";
import { useGetDropDownUnitCodesQuery } from "@/services/unitcode.service";
import { useGetDropDownTransactionTypesQuery } from "@/services/transactiontype.service";
import moment from "moment-timezone";
import Information from "./_information";
import { TransactionImage } from "@/types/transactionimage.type";
import { EnumStatus, TransactionStatus } from "@/constants/enum";
import { formatDateTimeAutoToString } from "@/utils/format";
import "./style.css"
import { useGetDropDownBusinessUnitsQuery } from "@/services/businessunit.service";
import { TransactionFieldForm } from "@/types/transaction.type";

export default function EditTransaction() {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [editTransaction, { isSuccess }] = useEditTransactionMutation()
    const {data: businessUnits} = useGetDropDownBusinessUnitsQuery({status: `${EnumStatus.ACTIVE},${EnumStatus.INAVTIVE}`, checkExpired: false})
    const {data: unitcodes} = useGetDropDownUnitCodesQuery(EnumStatus.ACTIVE)
    const {data: transactionTypes} = useGetDropDownTransactionTypesQuery(EnumStatus.ACTIVE)
    const {data: transaction} = useGetTransactionQuery(location.state?.id || "", {refetchOnMountOrArgChange: true})
    const methods = useForm<TransactionFieldForm>()
    const {setValue, handleSubmit} = methods
    const [files, setFiles] = useState<File[]>([]);
    const [images, setImages] = useState<TransactionImage[]>([])
    const listStatus = ListTransactionStatus();
    const listType = ListTypeTransaction();

    const onSubmit = handleSubmit(async (data) => {
        if(transaction) {
            const formData = new FormData();
            let dateTransaction = transaction.date
            if (formatDateTimeAutoToString(transaction.date, "YYYY-MM-DD") !== data.date) {
                let now = moment();
                dateTransaction = moment(`${data.date} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            formData.append("id", transaction.id)
            formData.append("business_unit_id", data.business_unit_id ? data.business_unit_id : uuidNull)
            formData.append("unitcode_id", data.unitcode_id)
            formData.append("transactiontype_id", data.transactiontype_id)
            formData.append("type", data.type.toString())
            formData.append("amount", data.amount)
            formData.append("description", data.description)
            formData.append("date", dateTransaction)
            let status = data.status
            if(transaction.status === TransactionStatus.WITHOUT_RECEIVE && files.length > 0) {
                status = TransactionStatus.FINISH
            } else if(status > TransactionStatus.WITHOUT_RECEIVE && files.length === 0 && images.length === 0) {
                status = TransactionStatus.WITHOUT_RECEIVE
            }
            formData.append("status", status?.toString())
            const removeImages = _.differenceBy(transaction.images, images, 'id')
            removeImages.forEach(item => {
                formData.append("image_ids[]", item.id)
            })
            if(!_.isEmpty(files)) {
                for (const file of files) {
                    formData.append('images[]', file)
                }
            }
            data.link_uploads.forEach((item, idx) => {
                if(!_.isEmpty(item.link)) {
                    formData.append(`link_uploads`, item.link)
                }
            })
            editTransaction(formData)
        }
    })

    useEffect(() => {
        if(isSuccess) {
            navigate(location, {state: {reload: true}})
        }
    }, [isSuccess])

    useEffect(() => {
        if(transaction) {
            setValue("business_unit_id", transaction.business_unit_id)
            setValue("amount", (transaction.amount_deposit + transaction.amount_withdraw).toString())
            setValue("type", _.find(listType, item => item.value === transaction.type)?.value || listType[0].value)
            setValue("description", transaction.description)
            setValue("date", formatDateTimeAutoToString(transaction.date, "YYYY-MM-DD"))
            setImages(transaction.images ? transaction.images.filter((item) => item.type === 1) : [])
            setValue("status", _.find(listStatus, item => item.value === transaction.status)?.value || listStatus[0].value)
            const linkUploads = transaction.images ? transaction.images.filter((item) => item.type === 2).map((item) => ({ link: item.image_url })) : []
            setValue("link_uploads", linkUploads.length > 0 ? linkUploads : [{ link: "" }])
        }
    }, [transaction])

    useEffect(() => {
        if(_.isArray(businessUnits)) {
            setValue("business_unit_id", transaction?.business_unit_id || uuidNull)
        }
    }, [businessUnits, transaction?.business_unit_id])

    useEffect(() => {
        if(_.isArray(unitcodes) && !_.isEmpty(transaction)) {
            let data = _.find(unitcodes, item => item.value === transaction.unitcode_id)
            data && setValue("unitcode_id", data.value)
        }
    }, [unitcodes, transaction])

    useEffect(() => {
        if(_.isArray(transactionTypes) && !_.isEmpty(transaction)) {
            let data = _.find(transactionTypes, item => item.value === transaction.transactiontype_id)
            data && setValue("transactiontype_id", data.value)
        }
    }, [transactionTypes, transaction])

    return (
        <div x-show="modalOpen" x-transition="" className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90">
            <div className="w-full max-w-screen-lg rounded-lg bg-white dark:bg-boxdark">
                <h3 className="text-xl dark:text-white text-center font-medium my-5 mt-8">
                    {t("transaction.edit_transaction")}
                </h3>
                <form className="mx-5" onSubmit={onSubmit}>
                    <FormProvider {...methods}>
                        <Information setFiles={setFiles} files={files} images={images} setImages={setImages} isEdit={true} />
                    </FormProvider>
                    <div className="flex justify-center my-8">
                        <div className="px-3">
                            <button type="button" onClick={() => navigate(location)} className="block w-full rounded border border-stroke bg-gray-400 p-1.5 px-3 text-center font-medium text-black transition hover:bg-opacity-90">
                                {t("modal.cancel")}
                            </button>
                        </div>
                        <div className="px-3">
                            <button type="submit" className="flex w-full justify-center rounded bg-primary p-1.5 px-3 text-white hover:bg-opacity-90">
                                {t("modal.submit")}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
